import './Coverages.css';

import React from 'react';
import ReactHtmlParser from 'react-html-parser';

import { lobTypeToProductLabel } from './configs/productLabels';
import { getCoveragesDetails } from './CoveragesDetails';

interface AvailableCoverageProps {
  /** The lobType offered but not currently active (e.g. 'EO'). */
  lobType: string;
  /**
   * Add this LOB to the quote via updateLob (reuse from ApplicationGroupAudit,
   * or create + redirect to the LOB survey). Optional so the card renders
   * disabled until the handler is wired.
   */
  onAdd?: (lobType: string) => void;
  /** Region of the active quote — drives the coverage-details config lookup. */
  region: string;
  /** Carrier partner of the active quote — drives the config lookup. */
  carrierPartner: string;
}

// The LOB-defining coverage name whose config (icon/title/subtitle) represents
// the LOB on the quote page: CGL for GL, EO for EO.
const PRIMARY_COVERAGE_NAME: Record<string, string> = {
  GL: 'cgl',
  EO: 'eo'
};

/**
 * Card for a LOB that was offered in underwriting or previously created but is
 * not currently active. Renders the same icon, title and description as the
 * real coverage card (from getCoveragesDetails) with an unchecked checkbox —
 * checking it adds the LOB (updateLob). No limits are shown until the LOB is
 * quoted. NCP-4545.
 */
export const AvailableCoverage: React.FC<AvailableCoverageProps> = ({
  lobType,
  onAdd,
  region,
  carrierPartner
}) => {
  const coverageName = PRIMARY_COVERAGE_NAME[lobType];
  const coverageDetails = coverageName
    ? getCoveragesDetails(region, carrierPartner, lobType)[coverageName]
    : undefined;

  const title = coverageDetails?.title ?? lobTypeToProductLabel(lobType);

  return (
    <div className="coverages-container mt-2 md:mt-0">
      <div className="coverages-inner-container px-8 pt-4 pb-8">
        <div className="coverage grid grid-cols-1 md:grid-cols-8 mb-5">
          <div className="col-span-1 grid grid-cols-4">
            <input
              type="checkbox"
              data-name={`${lobType}_add_checkbox`}
              className="flex justify-center items-center w-10 h-10 mt-2 col-span-1"
              checked={false}
              disabled={!onAdd}
              onChange={() => onAdd?.(lobType)}
            />
            <p className="col-span-3 md:hidden text-navy font-extrabold text-xl">
              {title}
            </p>
          </div>
          {coverageDetails?.img ? (
            <img
              src={coverageDetails.img}
              className="col-span-1 hidden md:block mxr-4 h-14"
            />
          ) : null}
          <div className="col-span-1 md:col-span-6 pl-2 md:pl-0">
            <p>
              <b className="hidden md:block text-navy font-extrabold text-base md:text-2xl">
                {title}
              </b>
            </p>
            {coverageDetails?.subTitle ? (
              <span className="coverage-description text-gray-700 mt-2 mb-2 text-xs md:text-base inline-block">
                {ReactHtmlParser(coverageDetails.subTitle)}
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AvailableCoverage;
